import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  CheckCircleOutline as ReviewIcon,
  Close as CloseIcon,
  Straighten as MeasurementsIcon,
  MonitorWeight as WeightIcon,
  CameraAlt as PhotoIcon,
  Description as FormIcon,
  FactCheck as CheckInIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {getCheckIns, deleteCheckIn, getCheckInStats, createCheckIn} from "../services/CheckInService";
import {useCompany} from "../contexts/CompanyContext";
import {useMessageContext} from "../contexts/MessageContext";
import {getErrorMessage} from "../enums/ErrorMessages";
import CreateCheckInDialog from "../componenets/check-ins/CreateCheckInDialog";
import ScheduledCheckInList from "../componenets/check-ins/ScheduledCheckInList";
import CheckInItemDialog from "../componenets/dashboard/coach/CheckInItemDialog";
import {Pagination} from "../componenets/ui";
import StatCard from "../componenets/ui/StatCard";
import TabsRow from "../componenets/dashboard/coach/TabsRow";
import {t} from "../componenets/dashboard/coach/coachTokens";
import { imageUrl } from "../utils/imageUrl";

/* ── Constants ───────────────────────────────────────────── */
const PAGE_SIZE = 20;

const ITEM_TYPE_ICONS = {
  BODY_MEASUREMENT: {icon: <MeasurementsIcon sx={{fontSize: "16px"}}/>, color: t.purple, label: "מדידות"},
  BODY_WEIGHT: {icon: <WeightIcon sx={{fontSize: "16px"}}/>, color: t.blue, label: "משקל"},
  PROGRESS_PHOTO: {icon: <PhotoIcon sx={{fontSize: "16px"}}/>, color: t.green, label: "תמונות"},
  FORM: {icon: <FormIcon sx={{fontSize: "16px"}}/>, color: t.amber, label: "טופס"},
};

const STATUS_CONFIG = {
  PENDING: {label: "ממתין למילוי", color: t.amberText, bgColor: t.amberLight},
  COMPLETED: {label: "הושלם", color: t.greenText, bgColor: t.greenLight},
  REVIEWED: {label: "נבדק", color: t.blueText, bgColor: t.blueLight},
  CANCELED: {label: "בוטל", color: t.redText, bgColor: t.redLight},
};

const STATUS_FILTERS = [
  {value: null, label: "הכל"},
  {value: "PENDING", label: "ממתין"},
  {value: "COMPLETED", label: "הושלם"},
  {value: "REVIEWED", label: "נבדק"},
  {value: "CANCELED", label: "בוטל"},
];

const CHECKIN_TABS = [
  {key: 0, label: "צ׳ק-אינים", Icon: CheckInIcon},
  {key: 1, label: "צ׳ק-אינים מתוזמנים", Icon: ScheduleIcon},
];

const formatDate = (epochSeconds) => {
  if (!epochSeconds) return "-";
  return new Date(epochSeconds * 1000).toLocaleDateString("he-IL");
};

/* ── Table cell presets (match Clients/CoachDashboard) ────── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

/* ═══════════════════════════════════════════════════════════
   Check-Ins page
   ═══════════════════════════════════════════════════════════ */
const CheckInsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {company} = useCompany();
  const {notificationHandler} = useMessageContext();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [activeTab, setActiveTab] = useState(0);
  const scheduledCreateRef = useRef(null);

  // Stats
  const [stats, setStats] = useState({pending: 0, completed: 0, reviewed: 0});

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Item dialog (click an item chip in the פריטים column)
  const [itemDialog, setItemDialog] = useState(null);

  const [f, setF] = useState({
    checkIns: [],
    total: 0,
    totalPages: 0,
    keyword: query.get("keyword") || "",
    status: query.get("status") || null,
    page: +(query.get("page") || 0),
    loading: true,
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
  }, []);

  // URL sync
  const sync = (ns) => {
    const p = new URLSearchParams();
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.status) p.set("status", ns.status);
    if (ns.page > 0) p.set("page", ns.page);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
  };

  const set = (patch) => { const ns = {...f, page: 0, ...patch}; setF(ns); sync(ns); };

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await getCheckInStats(company?.id);
      setStats(res.data || {pending: 0, completed: 0, reviewed: 0});
    } catch (e) {
      console.error("Error fetching stats:", e);
    }
  }, [company?.id]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Fetch check-ins
  useEffect(() => {
    setF(p => ({...p, loading: true}));
    getCheckIns(f.page, PAGE_SIZE, f.status, company?.id, f.keyword || null)
        .then(r => setF(p => ({
          ...p,
          checkIns: r.data?.content || [],
          total: r.data?.totalElements || 0,
          totalPages: r.data?.totalPages || 0,
          loading: false,
        })))
        .catch(() => setF(p => ({...p, loading: false})));
  }, [f.page, f.keyword, f.status, company]);

  const reload = () => {
    setF(p => ({...p, loading: true}));
    getCheckIns(f.page, PAGE_SIZE, f.status, company?.id, f.keyword || null)
        .then(r => setF(p => ({
          ...p,
          checkIns: r.data?.content || [],
          total: r.data?.totalElements || 0,
          totalPages: r.data?.totalPages || 0,
          loading: false,
        })))
        .catch(() => setF(p => ({...p, loading: false})));
    fetchStats();
  };

  const pages = Math.ceil(f.total / PAGE_SIZE);

  // Create handler
  const handleCreateSubmit = async (data) => {
    await createCheckIn(data);
    notificationHandler.success("צ'ק-אין נשלח בהצלחה");
    reload();
  };

  const handleCreateClick = () => {
    if (activeTab === 0) {
      setCreateDialogOpen(true);
    } else {
      scheduledCreateRef.current?.();
    }
  };

  // Delete handler
  const handleDeleteClick = (checkIn) => (e) => {
    e.stopPropagation();
    setDeleteTarget(checkIn);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteTarget) {
      try {
        await deleteCheckIn(deleteTarget.id);
        notificationHandler.success("צ'ק-אין נמחק בהצלחה");
        reload();
      } catch (e) {
        notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה במחיקת הצ'ק-אין");
      }
    }
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  /* ── Add button (shared by both tabs) ────────────────── */
  const addButton = (
      <Box component="button" onClick={handleCreateClick} sx={{
        display: "inline-flex", alignItems: "center", gap: "5px",
        background: t.green, color: "#fff",
        height: 34, px: "15px", borderRadius: t.rs,
        fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
        border: "none", cursor: "pointer", fontFamily: "inherit",
        transition: "all .14s",
        "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
      }}>
        <AddIcon sx={{fontSize: 16}}/>
        צ'ק-אין חדש
      </Box>
  );

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>

        {/* Create dialog */}
        <CreateCheckInDialog
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            onSubmit={handleCreateSubmit}
        />

        {/* Delete confirmation dialog */}
        <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth
                PaperProps={{sx: {borderRadius: t.r, direction: "rtl"}}}>
          <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderBottom: `1px solid ${t.border}`}}>
            <Typography sx={{fontSize: "16px", fontWeight: 700, color: t.text}}>מחיקת צ'ק-אין</Typography>
            <IconButton onClick={closeDeleteDialog} size="small" sx={{color: t.t2}}><CloseIcon/></IconButton>
          </DialogTitle>
          <DialogContent sx={{padding: "24px"}}>
            <Typography sx={{fontSize: "14px", color: t.t2}}>
              האם אתה בטוח שברצונך למחוק את הצ'ק-אין של {deleteTarget?.traineeName}?
            </Typography>
          </DialogContent>
          <DialogActions sx={{padding: "16px 24px", borderTop: `1px solid ${t.border}`, gap: 2}}>
            <Box component="button" onClick={closeDeleteDialog} sx={{
              flex: 1, height: 38, borderRadius: t.rs, fontSize: "13px", fontWeight: 600,
              background: t.white, color: t.t2, border: `1px solid ${t.border}`, cursor: "pointer", fontFamily: "inherit",
              "&:hover": {background: t.bg, borderColor: t.t3},
            }}>
              ביטול
            </Box>
            <Box component="button" onClick={handleDeleteConfirm} sx={{
              flex: 1, height: 38, borderRadius: t.rs, fontSize: "13px", fontWeight: 600,
              background: t.red, color: "#fff", border: "none", cursor: "pointer", fontFamily: "inherit",
              "&:hover": {background: t.redText},
            }}>
              מחק
            </Box>
          </DialogActions>
        </Dialog>

        {/* Item dialog */}
        <CheckInItemDialog
            open={!!itemDialog}
            onClose={() => setItemDialog(null)}
            checkInId={itemDialog?.checkInId}
            traineeId={itemDialog?.traineeId}
            itemType={itemDialog?.itemType}
        />

        {/* ─── Tabs (on canvas) ────────────────────────── */}
        <TabsRow value={activeTab} onChange={setActiveTab} tabs={CHECKIN_TABS}/>

        {/* ─── Title row (no white band — sits on canvas) ─── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
              צ'ק-אין
            </Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול צ'ק-אינים</Typography>
          </Box>
          {addButton}
        </Box>

        {/* ─── Tab 0: Check-ins ───────────────────────── */}
        {activeTab === 0 && (
            <Box sx={{
              background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
              boxShadow: t.sh, overflow: "hidden",
              display: "flex", flexDirection: "column",
            }}>

              {/* Filter bar inside card */}
              <Box sx={{
                display: "flex", alignItems: "center", gap: "7px",
                padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
                background: t.bg, flexWrap: "wrap", flexShrink: 0,
              }}>
                {/* Count pill */}
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  fontSize: "12px", fontWeight: 700, color: t.greenText,
                  background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
                  padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
                }}>
                  <CheckInIcon sx={{fontSize: 14}}/>
                  {f.total} צ'ק-אינים
                </Box>

                {/* Search */}
                <Box sx={{position: "relative"}}>
                  <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
                  <InputBase
                      value={searchInput}
                      onChange={e => handleSearch(e.target.value)}
                      placeholder="חיפוש לפי שם מתאמן..."
                      sx={{
                        width: 200,
                        padding: "6px 10px 6px 30px",
                        border: `1.5px solid ${t.border}`,
                        borderRadius: t.rs,
                        background: t.white,
                        color: t.text,
                        fontSize: "12px",
                        "& input": {padding: 0},
                        "& input::placeholder": {color: t.t3, opacity: 1},
                        "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                      }}
                  />
                </Box>

                {/* Status chips */}
                <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                  {STATUS_FILTERS.map(sf => {
                    const on = f.status === sf.value;
                    return (
                        <Chip key={sf.value || "all"} label={sf.label}
                              onClick={() => set({status: sf.value})}
                              sx={{
                                fontWeight: 600, fontSize: "12.5px", height: "30px",
                                backgroundColor: on ? t.green : t.greenLight,
                                color: on ? "#fff" : t.t2,
                                "&:hover": {backgroundColor: on ? t.greenDark : t.border},
                              }}/>
                    );
                  })}
                </Box>

                <Box sx={{flex: 1}}/>

                {/* Stats pills */}
                <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                  <StatCard label="ממתינים" value={stats.pending} accent={t.amber}/>
                  <StatCard label="הושלמו" value={stats.completed} accent={t.green}/>
                  <StatCard label="נבדקו" value={stats.reviewed} accent={t.blue}/>
                </Box>
              </Box>

              {/* Table area */}
              {f.loading && !f.checkIns.length ? (
                  <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
                    <CircularProgress size={32} sx={{color: t.green}}/>
                  </Box>
              ) : !f.checkIns.length ? (
                  <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                    <CheckInIcon sx={{fontSize: "48px", color: t.border}}/>
                    <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>
                      {f.status ? "אין צ'ק-אינים בסטטוס זה" : "אין צ'ק-אינים עדיין"}
                    </Typography>
                    <Typography sx={{color: t.t3, fontSize: "13px"}}>לחץ על "צ'ק-אין חדש" כדי לשלוח צ'ק-אין</Typography>
                  </Box>
              ) : (
                  <>
                    <TableContainer sx={{overflowY: "auto", maxHeight: 540}}>
                      <Table stickyHeader sx={{tableLayout: "fixed"}}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{...th, textAlign: "right"}}>מתאמן</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: "12%", display: {xs: "none", md: "table-cell"}}}>תאריך שליחה</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: "12%", display: {xs: "none", md: "table-cell"}}}>סטטוס</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: "15%", display: {xs: "none", md: "table-cell"}}}>פריטים</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: "8%", display: {xs: "none", md: "table-cell"}}}>התקדמות</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: 120}}>פעולות</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {f.checkIns.map(checkIn => {
                            const statusCfg = STATUS_CONFIG[checkIn.status] || STATUS_CONFIG.PENDING;
                            const completedCount = checkIn.items?.filter(i => i.completed).length || 0;
                            const totalCount = checkIn.items?.length || 0;
                            return (
                                <TableRow key={checkIn.id}
                                          sx={{cursor: "pointer", "&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}
                                          onClick={() => navigate(`/app/check-ins/${checkIn.id}`)}>
                                  {/* Trainee */}
                                  <TableCell sx={{...td, textAlign: "right"}}>
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                      <Avatar src={imageUrl(checkIn.traineePhotoUrl, 36)} sx={{width: 36, height: 36, fontSize: "14px", backgroundColor: t.green}}>
                                        {checkIn.traineeName?.[0]}
                                      </Avatar>
                                      <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "200px"}}>
                                        {checkIn.traineeName}
                                      </Typography>
                                    </Box>
                                  </TableCell>

                                  {/* Date */}
                                  <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                    <Typography sx={{fontSize: "13px", color: t.t2}}>
                                      {formatDate(checkIn.createdDate)}
                                    </Typography>
                                  </TableCell>

                                  {/* Status */}
                                  <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                    <Chip label={statusCfg.label} size="small" sx={{
                                      backgroundColor: statusCfg.bgColor, color: statusCfg.color, fontWeight: 600, fontSize: "12px",
                                    }}/>
                                  </TableCell>

                                  {/* Items */}
                                  <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}} onClick={e => e.stopPropagation()}>
                                    <Box sx={{display: "flex", gap: 0.5, justifyContent: "center"}}>
                                      {checkIn.items?.map((item, idx) => {
                                        const cfg = ITEM_TYPE_ICONS[item.type];
                                        if (!cfg) return null;
                                        return (
                                            <Tooltip key={idx} title={`${cfg.label}${item.completed ? " ✓" : ""}`}>
                                              <Box
                                                  component="button"
                                                  onClick={() => setItemDialog({checkInId: checkIn.id, traineeId: checkIn.traineeId, itemType: item.type})}
                                                  sx={{
                                                    width: 28, height: 28, borderRadius: t.rxs, padding: 0, cursor: "pointer", fontFamily: "inherit",
                                                    border: `1px solid ${item.completed ? "rgba(1,187,167,.3)" : t.border}`,
                                                    backgroundColor: `${cfg.color}15`, display: "flex", alignItems: "center", justifyContent: "center",
                                                    transition: "all .13s",
                                                    "& svg": {color: item.completed ? cfg.color : t.t3},
                                                    "&:hover": {borderColor: cfg.color, transform: "translateY(-1px)", boxShadow: `0 2px 8px ${cfg.color}33`},
                                                  }}>
                                                {cfg.icon}
                                              </Box>
                                            </Tooltip>
                                        );
                                      })}
                                    </Box>
                                  </TableCell>

                                  {/* Progress */}
                                  <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                    <Typography sx={{fontSize: "13px", color: t.t2, fontWeight: 600}}>
                                      {completedCount}/{totalCount}
                                    </Typography>
                                  </TableCell>

                                  {/* Actions */}
                                  <TableCell sx={{...td, textAlign: "center", width: 120}}>
                                    <Box sx={{display: "flex", justifyContent: "center", gap: "4px"}} onClick={e => e.stopPropagation()}>
                                      <IconButton size="small" onClick={() => navigate(`/app/check-ins/${checkIn.id}`)}
                                                  sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: `${t.green}18`}}}>
                                        <ViewIcon fontSize="small"/>
                                      </IconButton>
                                      {checkIn.status === "COMPLETED" && (
                                          <IconButton size="small" onClick={() => navigate(`/app/check-ins/${checkIn.id}`)}
                                                      sx={{color: t.green, "&:hover": {bgcolor: `${t.green}18`}}}>
                                            <ReviewIcon fontSize="small"/>
                                          </IconButton>
                                      )}
                                      <IconButton size="small" onClick={handleDeleteClick(checkIn)}
                                                  sx={{color: t.t2, "&:hover": {color: t.red, bgcolor: `${t.red}18`}}}>
                                        <DeleteIcon fontSize="small"/>
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {pages > 1 && (
                        <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={PAGE_SIZE}
                                    onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}/>
                    )}
                  </>
              )}
            </Box>
        )}

        {/* ─── Tab 1: Scheduled check-ins ─────────────── */}
        {activeTab === 1 && (
            <Box sx={{flex: 1, overflow: "auto"}}>
              <ScheduledCheckInList onCreateClick={scheduledCreateRef}/>
            </Box>
        )}
      </Box>
  );
};

export default CheckInsPage;
